/**
 * Per-document persistence of Accept/Reject decisions on AI edit operations.
 *
 * Keyed by `<messageId>/<operationId>` → 'accepted' | 'rejected'. Missing
 * keys imply 'pending'. Persisted in `Office.context.document.settings` so
 * a user who closes Word mid-review and reopens sees the same state.
 */

type Status = 'accepted' | 'rejected';
type StoreShape = Record<string, Status>;

const KEY = 'nyayassist:editReviewState';

function load(): StoreShape {
  try {
    const raw = Office.context.document.settings.get(KEY) as unknown;
    if (raw && typeof raw === 'object') return raw as StoreShape;
  } catch {
    /* Office not ready — treat as empty */
  }
  return {};
}

function keyFor(messageId: string, operationId: string): string {
  return `${messageId}/${operationId}`;
}

export function getReviewStatus(messageId: string, operationId: string): Status | undefined {
  return load()[keyFor(messageId, operationId)];
}

export function getReviewStatusesForMessage(messageId: string): Record<string, Status> {
  const all = load();
  const prefix = `${messageId}/`;
  const out: Record<string, Status> = {};
  for (const [k, v] of Object.entries(all)) {
    if (k.startsWith(prefix)) out[k.slice(prefix.length)] = v;
  }
  return out;
}

export async function setReviewStatus(
  messageId: string,
  operationId: string,
  status: Status,
): Promise<void> {
  const all = load();
  all[keyFor(messageId, operationId)] = status;
  Office.context.document.settings.set(KEY, all);
  await persist();
}

export async function setReviewStatusBulk(
  messageId: string,
  ids: string[],
  status: Status,
): Promise<void> {
  if (ids.length === 0) return;
  const all = load();
  for (const id of ids) all[keyFor(messageId, id)] = status;
  Office.context.document.settings.set(KEY, all);
  await persist();
}

function persist(): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.settings.saveAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message ?? 'settings.saveAsync failed'));
        return;
      }
      resolve();
    });
  });
}
